import React, { type SetStateAction, useEffect, useState } from "react";
import "./SpecForm.scss";
import { ManagedInput } from "components/orders/Fields/Input/ManagedInput";
import type { ObstructionTypeArray } from "./SpecFormSchema";
import { OrderContext } from "context/OrderContext";
import {
  getCoverExtensionFeatureId,
  findFeatureByObstructionLabel,
  getAnchorTypeFeatureId,
  getLagFeatureId,
  ANCHOR_NA,
  getAnchorTypeOptions,
} from "../Features/Feature/FeatureConstants";
import { useT } from "i18n";

let changedObstructionFeatureId = false;
interface ObstructionProps {
  setObstruction: (obstruction: ObstructionTypeArray) => void;
  obstruction: ObstructionTypeArray | null;
  archived?: boolean;
}

export interface obstructionValuesType {
  id: string;
  anchorTypeOptions?: string;
  cableAssembly?: boolean;
  isRemovable?: boolean;
  isCovered?: boolean;
  isOverWater?: boolean;
  notes?: string;
}
const getObstructionFeatureId = (obstruction: ObstructionTypeArray | null) => {
  // If user has manually edited the feature ID, preserve their edit
  if (changedObstructionFeatureId) {
    return obstruction?.obstructionFeatureId;
  }

  const { state } = React.useContext(OrderContext);
  const changedFeatures = state?.features.features;
  const newAnchorTypeOptions = getAnchorTypeOptions(obstruction, changedFeatures);

  // prioritize these anchor types
  if (newAnchorTypeOptions && newAnchorTypeOptions !== ANCHOR_NA) {
    const lagFeatureId = getLagFeatureId(newAnchorTypeOptions);
    if (lagFeatureId) {
      return lagFeatureId;
    }
  }

  // everything below are part of the new features
  // https://github.com/BrightDotAi/latham-cloud-api/blob/main/targets/client/src/components/orders/Forms/Partials/Spec/ObstructionFeat.tsx
  // find the matching feature

  const feature = findFeatureByObstructionLabel(obstruction, changedFeatures);

  // Because cover extension is a special case, we need to check for it first
  const coverExtensionFeatureId = getCoverExtensionFeatureId(feature?.coverExtension);
  if (coverExtensionFeatureId) {
    return coverExtensionFeatureId;
  }

  // check anchor type
  const anchorFeatureId = getAnchorTypeFeatureId(obstruction, changedFeatures);
  if (anchorFeatureId) {
    return anchorFeatureId;
  }

  // fallback to existing obstruction feature ID otherwise
  return obstruction?.obstructionFeatureId;
};
const Obstruction = ({ obstruction, setObstruction, archived }: ObstructionProps) => {
  const t = useT();
  // Field states
  const [obstructionComments, setObstructionComments] = useState<string | undefined>(
    obstruction?.obstructionComments,
  );
  const [obstructionFeatureId, setObstructionFeatureId] = useState<string | undefined>(
    getObstructionFeatureId(obstruction),
  );
  const [obstructionHeight, setObstructionHeight] = useState<string | undefined>(
    obstruction?.obstructionHeight?.toString(),
  );
  const [obstructionLabel, setObstructionLabel] = useState<string | undefined>(
    obstruction?.obstructionLabel,
  );
  const [obstructionLength, setObstructionLength] = useState<string | undefined>(
    obstruction?.obstructionLength?.toString(),
  );
  const [obstructionSpaCoveredFeatureSId, setObstructionSpaCoveredFeatureSId] =
    useState<string | undefined>(obstruction?.obstructionSpaCoveredFeatureSId);
  const [obstructionType, setObstructionType] = useState<string | undefined>(
    obstruction?.obstructionType,
  );
  const [obstructionUpOverFeatureSId, setObstructionUpOverFeatureSId] = useState<
    string | undefined
  >(obstruction?.obstructionUpOverFeatureSId);

  const updateObstructionFeatureId = (
    obstructionFeatureId: SetStateAction<string | undefined>,
  ) => {
    changedObstructionFeatureId = true;
    setObstructionFeatureId(obstructionFeatureId);
  };
  useEffect(() => {
    setObstruction({
      obstructionComments: obstructionComments ?? "",
      obstructionFeatureId: obstructionFeatureId ?? "",
      obstructionHeight: parseInt(obstructionHeight ?? "0"),
      obstructionLabel: obstructionLabel ?? "",
      obstructionLength: parseInt(obstructionLength ?? "0"),
      obstructionSpaCoveredFeatureSId: obstructionSpaCoveredFeatureSId ?? "",
      obstructionType: obstructionType ?? "",
      obstructionUpOverFeatureSId: obstructionUpOverFeatureSId ?? "",
    });
  }, [
    obstructionComments,
    obstructionFeatureId,
    obstructionHeight,
    obstructionLabel,
    obstructionLength,
    obstructionSpaCoveredFeatureSId,
    obstructionType,
    obstructionUpOverFeatureSId,
  ]);

  return (
    <>
      {obstructionLabel && (
        <div className={"spec-form__second-title"}>
          {t("component.forms.spec.featureLabel", "Feature: {{label}}", {
            label: obstructionLabel,
          })}
        </div>
      )}
      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionComments", "Obstruction Comments")}
        type={"text"}
        placeholder={t(
          "component.forms.spec.obstructionComments",
          "Obstruction Comments",
        )}
        onChange={setObstructionComments}
        value={obstructionComments}
        disabled={archived}
      />

      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionFeatureId", "Obstruction FeatureId")}
        type={"text"}
        placeholder={t(
          "component.forms.spec.obstructionFeatureId",
          "Obstruction FeatureId",
        )}
        onChange={updateObstructionFeatureId}
        value={obstructionFeatureId}
        disabled={archived}
      />

      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionHeight", "Obstruction Height")}
        type={"text"}
        placeholder={t("component.forms.spec.obstructionHeight", "Obstruction Height")}
        onChange={setObstructionHeight}
        value={obstructionHeight}
        disabled={archived}
      />
      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionLabel", "Obstruction Label")}
        type={"text"}
        placeholder={t("component.forms.spec.obstructionLabel", "Obstruction Label")}
        onChange={setObstructionLabel}
        value={obstructionLabel}
        disabled={archived}
      />
      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionLength", "Obstruction Length")}
        type={"text"}
        placeholder={t("component.forms.spec.obstructionLength", "Obstruction Length")}
        onChange={setObstructionLength}
        value={obstructionLength}
        disabled={archived}
      />
      <ManagedInput
        isValid={true}
        label={t(
          "component.forms.spec.obstructionSpaFeatureSId",
          "Obstruction Spa FeatureSId",
        )}
        type={"text"}
        placeholder={t(
          "component.forms.spec.obstructionSpaFeatureSId",
          "Obstruction Spa FeatureSId",
        )}
        onChange={setObstructionSpaCoveredFeatureSId}
        value={obstructionSpaCoveredFeatureSId}
        disabled={archived}
      />
      <ManagedInput
        isValid={true}
        label={t("component.forms.spec.obstructionType", "Obstruction Type")}
        type={"text"}
        placeholder={t("component.forms.spec.obstructionType", "Obstruction Type")}
        onChange={setObstructionType}
        value={obstructionType}
        disabled={archived}
      />
      <ManagedInput
        isValid={true}
        label={t(
          "component.forms.spec.obstructionUpAndOverFeatureSId",
          "Obstruction Up and Over FeatureSId",
        )}
        type={"text"}
        placeholder={t(
          "component.forms.spec.obstructionUpAndOverFeatureSId",
          "Obstruction Up and Over FeatureSId",
        )}
        onChange={setObstructionUpOverFeatureSId}
        value={obstructionUpOverFeatureSId}
        disabled={archived}
      />
    </>
  );
};

export default Obstruction;
